import { Link } from "@tanstack/react-router";
import { Sparkles } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { accentFor, type StoreListing } from "@/api/store.api";
import { cn } from "@/lib/utils";
import { ListingIcon } from "./listing-icon";

/** Width of one card plus the gap between cards. */
export function cardStep(el: HTMLElement, gap = 16) {
  const first = el.firstElementChild as HTMLElement | null;
  return first ? first.offsetWidth + gap : 0;
}

/** Scroll-snapped hero cards tinted by each listing's accent, as on the phone. */
export function FeaturedCarousel({ listings }: { listings: StoreListing[] }) {
  const scroller = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(0);

  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const onScroll = () => {
      const step = cardStep(el);
      if (step) setPage(Math.round(el.scrollLeft / step));
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [listings.length]);

  if (listings.length === 0) return null;

  return (
    <div className="space-y-3">
      <div
        ref={scroller}
        className="-mx-4 flex snap-x snap-mandatory scroll-px-4 gap-4 overflow-x-auto px-4 pb-1 [scrollbar-width:none] sm:-mx-0 sm:scroll-px-0 sm:px-0 [&::-webkit-scrollbar]:hidden"
      >
        {listings.map(listing => (
          <Link
            key={listing.packageName}
            to="/$packageName"
            params={{ packageName: listing.packageName }}
            className="relative flex aspect-[1.9] w-[88%] shrink-0 snap-start flex-col justify-between overflow-hidden rounded-3xl p-5 text-white sm:aspect-[2.4] sm:w-[calc(50%-8px)] lg:w-[calc(33.333%-11px)] lg:aspect-[1.7]"
            style={{ backgroundImage: `linear-gradient(135deg, ${accentFor(listing.packageName)} 0%, #0b0f19 100%)` }}
          >
            <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-black/30 px-2.5 py-1 text-[11px] font-semibold">
              <Sparkles className="size-3" />
              Featured
            </span>
            <div className="flex items-end gap-3">
              <ListingIcon listing={listing} size={56} className="shadow-lg" />
              <div className="min-w-0 flex-1">
                <div className="truncate font-display text-xl font-bold leading-6">{listing.name}</div>
                <p className="mt-0.5 line-clamp-2 text-[13px] leading-[17px] text-white/80">{listing.tagline}</p>
              </div>
            </div>
          </Link>
        ))}
      </div>
      {listings.length > 1 ? (
        <div className="flex justify-center gap-1.5">
          {listings.map((listing, index) => (
            <span
              key={listing.packageName}
              className={cn("h-1.5 rounded-full transition-all", index === page ? "w-4 bg-[#087d50]" : "w-1.5 bg-[#d9dcd7]")}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
